import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  AuthStore,
  hideInstallationsAnnouncementBannerKey,
  RemoteConfigActions,
  RemoteConfigSelectors,
  temporarilyHideInstallationsAnnouncementBanner,
} from '@store';

import { RemoteConfigBanners } from '@vi-iot/client';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RemoteConfigStore {
  state$ = this.store.select(RemoteConfigSelectors.getState());
  isLoaded$ = this.store.select(RemoteConfigSelectors.selectLoaded);
  remoteConfig$ = this.store.select(RemoteConfigSelectors.getRemoteConfig());
  hasAccessToProPlusFeaturesByVkOrg$ = this.store.select(
    RemoteConfigSelectors.hasAccessToProPlusFeaturesByVkOrg()
  );
  hasAccessToProPlusFeaturesByCompanyId$ = this.store.select(
    RemoteConfigSelectors.hasAccessToProPlusFeaturesByCompanyId()
  );
  isLearningRoomBannerAvailable$ = this.store.select(
    RemoteConfigSelectors.isLearningRoomBannerAvailable()
  );
  isE3HeatPumpMarketLaunchBannerVisible$ = this.store.select(
    RemoteConfigSelectors.isE3HeatPumpMarketLaunchBannerVisible()
  );
  isNewVitoguideFeaturesBannerAvailable$ = this.store.select(
    RemoteConfigSelectors.isNewVitoguideFeaturesBannerAvailable()
  );
  isPaidFeaturesBannerAvailable$ = this.store.select(
    RemoteConfigSelectors.isPaidFeaturesBannerAvailable()
  );
  hasAccessToWholeInstallationsTable$ = this.store.select(
    RemoteConfigSelectors.hasAccessToWholeInstallationsTable()
  );
  isBookingProButtonEnabled$ = this.store.select(RemoteConfigSelectors.isBookingProButtonEnabled());
  isBookingPlusButtonEnabled$ = this.store.select(
    RemoteConfigSelectors.isBookingPlusButtonEnabled()
  );
  isFlatRate$ = this.store.select(RemoteConfigSelectors.isFlatRate);

  appInsightsUserLogLevel$ = this.store.select(RemoteConfigSelectors.appInsightsUserLogLevel);
  isHardwareBundlingAvailable$ = this.store.select(
    RemoteConfigSelectors.isHardwareBundlingAvailable
  );
  azureCertificateFirmwareCheck$ = this.store.select(
    RemoteConfigSelectors.azureCertificateFirmwareCheck
  );
  isVicarePlansTileAvailable$ = this.store.select(RemoteConfigSelectors.isVicarePlansTileAvailable);
  isSmartHomeComponentsAvailable$ = this.store.select(
    RemoteConfigSelectors.isSmartHomeComponentsAvailable
  );
  isSmartHomeComponentsUsingMock$ = this.store
    .select(RemoteConfigSelectors.isSmartHomeComponentsUsingMock)
    .pipe(
      tap((value) => {
        this.isSmartHomeComponentsUsingMockSync = value;
      })
    );
  isSmartHomeComponentsUsingMockSync = true;
  isApplicationBetaVersionAvailable$ = this.store.select(
    RemoteConfigSelectors.isApplicationBetaVersionAvailable
  );

  isEcotronicAvailable$ = this.store.select(RemoteConfigSelectors.isEcotronicAvailable);

  shouldFetchEnergyConsumptionDataForLegacyHeatpump$ = this.store.select(
    RemoteConfigSelectors.shouldFetchEnergyConsumptionDataForLegacyHeatpump
  );

  isRestrictedAccessForTestAutomationInstallationsEnabled$ = this.store.select(
    RemoteConfigSelectors.isRestrictedAccessForTestAutomationInstallationsEnabled
  );

  countriesWithSmartHomeComponent$ = this.store.select(
    RemoteConfigSelectors.selectCountriesWithSmartHomeComponent
  );

  typeOfBookingPackage$ = this.store.select(RemoteConfigSelectors.selectTypeOfBookingPackage());

  isAppInsightsLoggingDataAboutDhw$ = this.store.select(
    RemoteConfigSelectors.selectIsAppInsightsLoggingDataAboutDhw
  );

  isBoilerSerialEditable$ = this.store.select(RemoteConfigSelectors.isBoilerSerialEditable);

  blockEndCustomers$ = this.store.select(RemoteConfigSelectors.selectBlockEndCustomers);

  showGenericInfoBanner$ = this.store.select(RemoteConfigSelectors.selectShowGenericInfoBanner);

  isSurveyBannerVisible$ = this.store.select(RemoteConfigSelectors.selectIsSurveyBannerVisible);

  isDomainSwitchInformationBannerVisible$ = this.store.select(
    RemoteConfigSelectors.selectIsDomainSwitchInformationBannerVisible
  );

  rsaViUnifyAccessIds$ = this.store.select(RemoteConfigSelectors.selectRsaViUnifyAccessIds);

  heatingSystemsAccess$ = this.store.select(RemoteConfigSelectors.selectHeatingSystemsAccess());
  isHeatingSystemsLinkVisibleInSideMenu$ = this.store.select(
    RemoteConfigSelectors.selectIsHeatingSystemsLinkVisibleInSideMenu
  );

  isWhatsAppBannerVisible$ = this.store.select(RemoteConfigSelectors.selectIsWhatsAppBannerVisible);

  constructor(private store: Store, private authStore: AuthStore) {}

  isBannerVisible(banner: RemoteConfigBanners): Observable<boolean> {
    return this.store.select(RemoteConfigSelectors.isBannerVisible(banner));
  }

  getRemoteConfig(appId: string): void {
    this.store.dispatch(RemoteConfigActions.getRemoteConfig({ appId }));
  }

  showBanner(isBannerHidden: boolean, banner: RemoteConfigBanners): void {
    return this.store.dispatch(RemoteConfigActions.showBanner({ isBannerHidden, banner }));
  }

  closeBanner(banner: RemoteConfigBanners): void {
    return this.store.dispatch(RemoteConfigActions.closeBanner({ banner }));
  }

  showInstallationsAnnouncementBanner(): Observable<boolean> {
    const token: string = this.authStore.getToken();
    const hidePermanently: string = localStorage.getItem(hideInstallationsAnnouncementBannerKey);
    const temporarilyHideForToken: string = localStorage.getItem(
      temporarilyHideInstallationsAnnouncementBanner
    );

    return this.store.select(
      RemoteConfigSelectors.showInstallationsAnnouncementBanner({
        token,
        hidePermanently,
        temporarilyHideForToken,
      })
    );
  }
}
